// @react
import { useEffect, useContext } from 'react'
// @types
import {
  GetUserQueryResult,
  UserEducationDeleteMutation,
  UserEducationDeleteMutationVariables,
  UserEducationUpsertMutation,
  UserEducationUpsertMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { EducationFormValues } from 'modules/common/forms/types/education'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @libraries
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { vsprintf } from 'sprintf-js'
// @graphQL
import { useMutation } from '@apollo/client'
import {
  USER_EDUCATION_DELETE,
  USER_EDUCATION_UPDATE,
} from 'graphql/mutations/user.mutation'
// @commons
import strings from 'lib/constants/strings'
import Helper from 'utils/Helper.class'
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
// @module component
import { validationSchema } from 'modules/common/forms/validations/education'

type Inputs = any

type SelfProps = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: any
}
type PropsType = SelfProps

const useEducationFormWrapper = <T extends Inputs>({
  data: userData,
  refetch,
}: PropsType) => {
  /******************************************
   * HOOKS
   ******************************************/

  // React Form Hook
  const formMethods = useForm<EducationFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      Education: [
        {
          degreeType: '',
          programType: '',
          schoolName: '',
          graduationYear: '',
          educationId: '',
        },
      ],
    },
  })
  const { control, reset } = formMethods
  const user = userData?.user
  useEffect(() => {
    if (user) {
      reset({
        Education: user?.education.length
          ? user?.education.map((item) => {
              return {
                degreeType: item.degreeType || '',
                programType: item.programType || '',
                schoolName: item.schoolName || '',
                graduationYear: Helper.getYearFromIso(item.endDate) || '',
                educationId: item.id || '',
              }
            })
          : [
              {
                degreeType: '',
                programType: '',
                schoolName: '',
                graduationYear: '',
                educationId: '',
              },
            ],
      })
    }
  }, [reset, user])

  /******************************************
   * GQL HOOKS
   ******************************************/

  // Mutation
  const [upsertUserEducation] = useMutation<
    UserEducationUpsertMutation,
    UserEducationUpsertMutationVariables
  >(USER_EDUCATION_UPDATE)

  const [deleteUserEducation] = useMutation<
    UserEducationDeleteMutation,
    UserEducationDeleteMutationVariables
  >(USER_EDUCATION_DELETE)

  /******************************************
   * HOOKS
   ******************************************/
  const snackbar = useContext(SnackbarContext)

  /******************************************
   * FUNCTIONS
   ******************************************/

  // delete userEducation
  const userEducationDeleteHandler = async (
    id: number,
    remove: (index?: number | number[]) => void,
  ) => {
    // educationID is cast to string from scalars[ID]
    // we'll convert to int here before submitting it
    const values = formMethods.getValues()
    const educationId = values.Education[id]?.educationId

    if (educationId) {
      const { errors: mutationErrors } = await deleteUserEducation({
        variables: {
          input: {
            educationId: parseInt(educationId),
          },
        },
      })

      if (!mutationErrors) {
        snackbar.setMessage({
          type: SnackbarTypes.SUCCESS,
          message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
            'Education',
          ]),
          show: true,
        })
        // update the UI to optimistically remove the education item from data array
        remove(id)
      } else {
        snackbar.setMessage({
          type: SnackbarTypes.ERROR,
          message: strings.ERRORS.GENERIC_UPDATE_ERROR,
          show: true,
        })
        console.log('mutation error', mutationErrors)
      }
    } else {
      remove(id)
    }
  }

  // update userEducation
  const userEducationUpdateHandler = async (
    data: EducationFormValues,
  ): Promise<boolean> => {
    //let shouldRefetch = false

    const { errors: mutationErrors } = await upsertUserEducation({
      variables: {
        input: data.Education.map((item) => {
          //if (!item.educationId) shouldRefetch = true
          return {
            educationId: item.educationId ? parseInt(item.educationId) : null,
            degreeType: item.degreeType,
            programType: item.programType,
            schoolName: item.schoolName,
            startDate: item.graduationYear,
            endDate: item.graduationYear,
          }
        }),
      },
    })

    if (!mutationErrors) {
      // @this is pretty clumsy, we are doing this to make sure we get back new ids.
      // @todo we should take the time to create an education only fragment query
      // then we should get that back and update the cache
      await refetch()
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Education',
        ]),
        show: true,
      })
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
    return false
  }

  /******************************************
   * RENDER
   ******************************************/

  return {
    formMethods,
    control,
    userEducationDeleteHandler,
    userEducationUpdateHandler,
  }
}

export default useEducationFormWrapper
